/**
 * BasePaymentProvider - Interface abstraite pour tous les providers de paiement
 *
 * Cette classe définit l'interface commune que tous les providers doivent implémenter.
 * Chaque provider (Stripe, PayPal, etc.) étend cette classe.
 */
export class BasePaymentProvider {
  constructor(config) {
    this.id = config.id;
    this.name = config.name;
    this.enabled = config.enabled ?? true;
    this.icon = config.icon;
  }

  /**
   * Initialise le provider (charge les SDK, configure les tokens, etc.)
   * @returns {Promise<any>} Le provider initialisé
   */
  async initialize() {
    throw new Error(`initialize() not implemented in ${this.id}`);
  }

  /**
   * Crée un paiement (génère un PaymentIntent, Order ID, etc.)
   * @param {number} amount - Montant en cents
   * @param {string} currency - Code devise (USD, EUR, XOF, etc.)
   * @param {object} metadata - Métadonnées du paiement
   * @returns {Promise<object>} Données du paiement créé
   */
  async createPayment(amount, currency, metadata) {
    throw new Error(`createPayment() not implemented in ${this.id}`);
  }

  /**
   * Traite le paiement (confirme, capture, etc.)
   * @param {object} paymentData - Données spécifiques au provider
   * @returns {Promise<object>} Résultat du paiement
   */
  async processPayment(paymentData) {
    throw new Error(`processPayment() not implemented in ${this.id}`);
  }

  /**
   * Valide les données de paiement avant traitement
   * @param {object} data - Données à valider
   * @returns {boolean} True si valide
   */
  validatePaymentData(data) {
    return true;
  }

  /**
   * Formate le montant pour le provider (conversion devise si nécessaire)
   * @param {number} amount - Montant original en cents
   * @param {string} fromCurrency - Devise source
   * @returns {number} Montant formaté pour le provider
   */
  formatAmount(amount, fromCurrency) {
    return amount;
  }

  /**
   * Vérifie si le provider est activé
   * @returns {boolean}
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Retourne l'ID du provider
   * @returns {string}
   */
  getId() {
    return this.id;
  }

  /**
   * Retourne le nom du provider
   * @returns {string}
   */
  getName() {
    return this.name;
  }

  /**
   * Retourne l'URL de l'icône du provider
   * @returns {string}
   */
  getIcon() {
    return this.icon;
  }

  /**
   * Nettoie les ressources du provider (à appeler dans onUnmounted)
   */
  destroy() {
    // Override in subclasses if needed
  }
}
